import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    LinearProgress,
    Tooltip,
    Card,
    Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { fmtL } from "../../../utils/currency";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import { ACTIVE_MODEL } from "../../../config/modelConfig";
import { BALANCE_SHEET_FACTOR, hasBalanceSheetGrade } from "../../../config/factors";

import { searchStockName, getRecentlyViewed, clearRecentlyViewed, removeTickerfromRecentlyViewed } from "../../../api/api";
import { getFactorRatings, getFactorRatingsAnalysis } from "../../../api/stocks_api";
import { refreshToken } from "../../../api/auth_api";

import { useNavigate } from "react-router-dom";
import AddToWatchlistDialog from "../../IndividualStocks/Widgets/AddToWatchlistDialog";
import { useTheme } from "@mui/material/styles";


const PRESETS = {
    TR: {
        tech: ['LOGO.IS', 'VESBE.IS', 'KAREL.IS'],
        auto: ['FROTO.IS', 'TOASO.IS', 'OTKAR.IS'],
    },
    DEFAULT: {
        tech: ['AAPL', 'MSFT', 'GOOGL'],
        auto: ['TSLA', 'F', 'GM'],
    },
};

const SEARCH_PLACEHOLDER = {
    TR: 'Search stocks to analyze (e.g. LOGO.IS, FROTO.IS, THYAO.IS)...',
    DEFAULT: 'Search stocks to analyze (e.g., NVDA, MSFT, LLY)...',
};

const getMarket = () => sessionStorage.getItem('selectedMarket') ?? 'US';
const getPresets = () => PRESETS[getMarket()] ?? PRESETS.DEFAULT;
const getPlaceholder = () => SEARCH_PLACEHOLDER[getMarket()] ?? SEARCH_PLACEHOLDER.DEFAULT;


const QuantFactorRating = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [recentStocks, setRecentStocks] = useState([]);
    const [searchedStocks, setSearchedStocks] = useState([]);
    const [factorRatings, setFactorRatings] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingRatings, setLoadingRatings] = useState({});
    const [searchInput, setSearchInput] = useState("");
    const [clearingRecent, setClearingRecent] = useState(false);

    // Search autocomplete states
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const searchRef = useRef(null);

    const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
    const [selectedTicker, setSelectedTicker] = useState(null);
    const [factorAnalysis, setFactorAnalysis] = useState({});
    const [loadingAnalysis, setLoadingAnalysis] = useState({});

    const FACTOR_TOOLTIPS = {
        VALUATION:
            "Measures how attractively the stock is priced relative to earnings, book value, sales, and free cash flow. A high FA grade means the stock trades at a discount to its intrinsic value.",
        GROWTH:
            "Evaluates revenue, earnings, and cash-flow growth trajectories over recent quarters and years. Stocks with accelerating, consistent growth score higher.",
        // FINANCIAL:
        // "Assesses balance-sheet strength, profitability margins, return on equity, and debt coverage. A top grade signals a financially healthy, efficiently run business.",
        [ACTIVE_MODEL === "return" ? "PROFITABILITY" : "QUALITY"]:
            ACTIVE_MODEL === "return"
                ? "Measures return efficiency, margins, ROE, and capital productivity."
                : "Ranks company quality based on profitability consistency and balance sheet strength.",
        /* Risk is now PRICE risk only — leverage / coverage moved to Balance-Sheet Quality */
        RISK:
            "Quantifies price risk — beta against the market, return volatility, Sharpe ratio and value-at-risk. Lower downside exposure improves this FA grade.",
        [BALANCE_SHEET_FACTOR.labelUpper]: BALANCE_SHEET_FACTOR.tooltip,
        MOMENTUM:
            "Tracks price momentum and earnings-revision trends relative to the broader market. Stocks with sustained upward price action and rising estimates score well.",
        SENTIMENT:
            "Incorporates analyst rating changes, short-interest shifts, and real-time news sentiment. Positive coverage upgrades and improving sentiment lift this FA grade.",
    };

    // const fetchFactorAnalysis = async (ticker) => {
    //     setLoadingAnalysis(prev => ({ ...prev, [ticker]: true }));

    //     try {
    //         // Get the factor ratings data for this ticker
    //         const ratingsData = factorRatings[ticker];

    //         if (!ratingsData) {
    //             console.error(`No ratings data found for ${ticker}`);
    //             setLoadingAnalysis(prev => ({ ...prev, [ticker]: false }));
    //             return;
    //         }

    //         // Structure payload according to new API format
    //         const payload = {
    //             ticker: ratingsData.ticker,
    //             overall_score: ratingsData.overall_score,
    //             overall_rating: ratingsData.overall_rating,
    //             valuation_grade: ratingsData.valuation_grade,
    //             growth_grade: ratingsData.growth_grade,
    //             financial_profit_grade: ratingsData.financial_profit_grade,
    //             risk_grade: ratingsData.risk_grade,
    //             momentum_grade: ratingsData.momentum_grade,
    //             ratios: ratingsData.ratios || null
    //         };

    //         const res = await getFactorRatingsAnalysis(payload);

    //         if (res?.success && res?.data) {
    //             setFactorAnalysis(prev => ({
    //                 ...prev,
    //                 [ticker]: res.data
    //             }));
    //         }

    //     } catch (error) {
    //         console.error(`Error fetching analysis for ${ticker}:`, error);
    //     } finally {
    //         setLoadingAnalysis(prev => ({ ...prev, [ticker]: false }));
    //     }
    // };

    const openAddToWatchlist = (ticker) => {
        setSelectedTicker(ticker);
        setWatchlistDialogOpen(true);
    };

    const closeAddToWatchlist = () => {
        setWatchlistDialogOpen(false);
        setSelectedTicker(null);
    };

    useEffect(() => {
        fetchRecentlyViewed();
    }, []);

    const fetchRecentlyViewed = async () => {
        setLoading(true);
        const res = await getRecentlyViewed();
        if (res?.success) {
            // Get unique stocks (last 6)
            const uniqueStocks = [];
            const seen = new Set();

            for (const stock of res.data || []) {
                if (!seen.has(stock.ticker)) {
                    uniqueStocks.push(stock);
                    seen.add(stock.ticker);
                }
                if (uniqueStocks.length >= 6) break;
            }

            setRecentStocks(uniqueStocks);

            // Fetch factor ratings for each stock
            uniqueStocks.forEach(stock => {
                fetchFactorRating(stock.ticker);
            });
        }
        setLoading(false);
    };

    // Update fetchFactorRating to pass data directly
    const fetchFactorRating = async (ticker) => {
        setLoadingRatings(prev => ({ ...prev, [ticker]: true }));

        try {
            const res = await getFactorRatings(ticker);

            // Handle the new response structure
            if (res?.success && res?.response_data) {
                setFactorRatings(prev => ({
                    ...prev,
                    [ticker]: res.response_data
                }));
                // Pass the data directly instead of waiting for state update
                await fetchFactorAnalysis(ticker, res.response_data);
            } else {
                console.error(`Failed to fetch factor ratings for ${ticker}:`, res);
            }
        } catch (error) {
            console.error(`Error fetching factor ratings for ${ticker}:`, error);
        } finally {
            setLoadingRatings(prev => ({ ...prev, [ticker]: false }));
        }
    };

    // Update fetchFactorAnalysis to accept ratingsData as parameter
    const fetchFactorAnalysis = async (ticker, ratingsData) => {
        setLoadingAnalysis(prev => ({ ...prev, [ticker]: true }));

        try {
            if (!ratingsData) return;

            const payload = [
                {
                    ticker: ratingsData.ticker,
                    overall_score: ratingsData.overall_score,
                    overall_rating: ratingsData.overall_rating,
                    valuation_grade: ratingsData.valuation_grade,
                    growth_grade: ratingsData.growth_grade,
                    financial_profit_grade: ratingsData.financial_profit_grade,
                    risk_grade: ratingsData.risk_grade,
                    momentum_grade: ratingsData.momentum_grade,
                    /* Optional 6th factor — only sent once the backend returns a grade,
                       so narrative behaviour is unchanged while it is null. */
                    ...(hasBalanceSheetGrade(ratingsData)
                        ? { [BALANCE_SHEET_FACTOR.gradeKey]: ratingsData[BALANCE_SHEET_FACTOR.gradeKey] }
                        : {}),
                    ratios: ratingsData.ratios || {}   // ✅ important
                }
            ];

            const res = await getFactorRatingsAnalysis(payload);

            if (res?.success && res?.data?.length > 0) {
                setFactorAnalysis(prev => ({
                    ...prev,
                    [ticker]: res.data[0]   // ✅ store object, not array
                }));
            }

        } catch (error) {
            console.error(`Error fetching analysis for ${ticker}:`, error);
        } finally {
            setLoadingAnalysis(prev => ({ ...prev, [ticker]: false }));
        }
    };


    // const fetchFactorRating = async (ticker) => {
    //     setLoadingRatings(prev => ({ ...prev, [ticker]: true }));

    //     try {
    //         const res = await getFactorRatings(ticker);

    //         // Handle the new response structure
    //         if (res?.success && res?.response_data) {
    //             setFactorRatings(prev => ({
    //                 ...prev,
    //                 [ticker]: res.response_data
    //             }));
    //             // Fetch analysis after getting ratings
    //             await fetchFactorAnalysis(ticker);
    //         } else {
    //             console.error(`Failed to fetch factor ratings for ${ticker}:`, res);
    //         }
    //     } catch (error) {
    //         console.error(`Error fetching factor ratings for ${ticker}:`, error);
    //     } finally {
    //         setLoadingRatings(prev => ({ ...prev, [ticker]: false }));
    //     }
    // };

    // Debounced autocomplete search
    useEffect(() => {
        if (!searchInput.trim()) {
            setSearchResults([]);
            setSearchError("");
            setShowSearchDropdown(false);
            return;
        }

        const delay = setTimeout(async () => {
            setSearchLoading(true);
            setSearchError("");

            try {
                const res = await searchStockName(searchInput.trim());

                if (res.authExpired) {
                    const refreshed = await refreshToken();

                    if (refreshed.success) {
                        const retryRes = await searchStockName(searchInput.trim());

                        if (retryRes.success && Array.isArray(retryRes.data)) {
                            setSearchResults(retryRes.data);
                            setShowSearchDropdown(true);
                            return;
                        }
                    }

                    sessionStorage.clear();
                    navigate("/login", { replace: true });
                    return;
                }

                if (res.success && Array.isArray(res.data) && res.data.length > 0) {
                    setSearchResults(res.data);
                    setShowSearchDropdown(true);
                } else {
                    setSearchResults([]);
                    setShowSearchDropdown(true);
                }
            } catch (error) {
                setSearchError("Something went wrong while searching.");
            } finally {
                setSearchLoading(false);
            }
        }, 300);

        return () => clearTimeout(delay);
    }, [searchInput, navigate]);

    // Close search dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowSearchDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleStockSelect = async (stock) => {
        const isAlreadySearched = searchedStocks.some(s => s.ticker === stock.ticker);

        if (!isAlreadySearched) {
            setSearchedStocks(prev => [...prev, stock]);
            await fetchFactorRating(stock.ticker);
        }

        setSearchInput("");
        setShowSearchDropdown(false);
    };

    const handleExecute = async () => {
        if (searchInput.trim()) {
            const tickers = searchInput
                .split(',')
                .map(t => t.trim().toUpperCase())
                .filter(t => t.length > 0);

            const newSearchedStocks = tickers.map(ticker => ({
                ticker,
                company_name: ticker,
            }));

            const uniqueStocks = newSearchedStocks.filter(
                newStock => !searchedStocks.some(s => s.ticker === newStock.ticker)
            );

            setSearchedStocks(prev => [...prev, ...uniqueStocks]);

            for (const ticker of tickers) {
                await fetchFactorRating(ticker);
            }

            setSearchInput("");
            setShowSearchDropdown(false);
        }
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearchedStocks([]);
        setShowSearchDropdown(false);
    };

    const handleRemoveSearched = (ticker) => {
        setSearchedStocks(prev => prev.filter(s => s.ticker !== ticker));
    };

    const handleRemoveRecent = async (ticker) => {
        setRecentStocks(prev => prev.filter(s => s.ticker !== ticker));
        const response = await removeTickerfromRecentlyViewed(ticker);

        if (response.success) {
            fetchRecentlyViewed();
        } else {
            console.error("Failed to clear the stocks");
        }
    };

    const handleClearRecentlyViewed = async () => {
        setClearingRecent(true);
        const res = await clearRecentlyViewed();

        if (res.success) {
            setRecentStocks([]);
        } else {
            console.error("Failed to clear recently viewed stocks");
        }

        setClearingRecent(false);
    };

    const getQuantData = (ticker) => {
        const data = factorRatings[ticker];

        if (!data) {
            return {
                overall: 0,
                overallGrade: "N/A",
                overallRating: "N/A",
                pulse: 0,
                valuation: 0,
                valuationGrade: "N/A",
                growth: 0,
                growthGrade: "N/A",
                financial: 0,
                financialGrade: "N/A",
                risk: 0,
                riskGrade: "N/A",
                momentum: 0,
                momentumGrade: "N/A",
                balanceSheet: 0,
                balanceSheetGrade: "N/A",
            };
        }

        return {
            overall: fmtL(data.overall_score || 0, 1),
            overallGrade: data.overall_grade || "N/A",
            overallRating: data.overall_rating || "N/A",
            pulse: parseFloat(data.overall_score || 0),
            valuation: parseFloat(data.valuation_score || 0),
            valuationGrade: data.valuation_grade || "N/A",
            growth: parseFloat(data.growth_score || 0),
            growthGrade: data.growth_grade || "N/A",
            financial: parseFloat(data.financial_profit_score || 0),
            financialGrade: data.financial_profit_grade || "N/A",
            risk: parseFloat(data.risk_score || 0),
            riskGrade: data.risk_grade || "N/A",
            momentum: parseFloat(data.momentum_score || 0),
            momentumGrade: data.momentum_grade || "N/A",
            /* NEW 6th factor — "N/A" until the model backend pushes it */
            balanceSheet: parseFloat(data[BALANCE_SHEET_FACTOR.scoreKey] || 0),
            balanceSheetGrade: data[BALANCE_SHEET_FACTOR.gradeKey] || "N/A",
        };
    };

    const getColorForGrade = (grade) => {
        if (!grade) return theme.palette.grades.unknown;
        const g = theme.palette.grades;
        switch (grade.toUpperCase()) {
            case "A+": return g.aPlus;
            case "A": return g.a;
            case "A-": return g.aMinus;
            case "B+": return g.bPlus;
            case "B": return g.b;
            case "B-": return g.bMinus;
            case "C+": return g.cPlus;
            case "C": return g.c;
            case "C-": return g.cMinus;
            case "D+": return g.dPlus;
            case "D": return g.d;
            case "D-": return g.dMinus;
            case "E+": return g.ePlus;
            case "E": return g.e;
            case "E-": return g.eMinus;
            case "F": return g.f;
            default: return g.unknown;
        }
    };

    const TypingNarrative = ({ text, speed = 8 }) => {
        const [displayed, setDisplayed] = useState("");
        const [done, setDone] = useState(false);
        const idxRef = useRef(0);
        const rafRef = useRef(null);
        const lastTimeRef = useRef(null);

        useEffect(() => {
            if (!text) return;
            setDisplayed("");
            setDone(false);
            idxRef.current = 0;
            lastTimeRef.current = null;

            const step = (timestamp) => {
                if (!lastTimeRef.current) lastTimeRef.current = timestamp;
                const elapsed = timestamp - lastTimeRef.current;

                if (elapsed >= speed) {
                    const charsToAdd = Math.max(1, Math.floor(elapsed / speed));
                    idxRef.current = Math.min(idxRef.current + charsToAdd, text.length);
                    setDisplayed(text.slice(0, idxRef.current));
                    lastTimeRef.current = timestamp;
                }

                if (idxRef.current < text.length) {
                    rafRef.current = requestAnimationFrame(step);
                } else {
                    setDone(true);
                }
            };

            rafRef.current = requestAnimationFrame(step);
            return () => cancelAnimationFrame(rafRef.current);
        }, [text, speed]);

        if (!text) return null;

        if (done) {
            /* ── DONE: render the guaranteed-complete text ──
               No truncation possible — text prop is the full string. */
            return (
                <Typography
                    sx={{
                        color: "text.primary",
                        lineHeight: 1.7,
                        fontStyle: "italic",
                        fontSize: "var(--font-size-sm)",
                        display: "inline",
                    }}
                >
                    {`"${text}"`}
                </Typography>
            );
        }

        /* ── TYPING: plain pre-wrap, no parser, cursor at end ──
           Prevents any mid-stream render glitch from cutting off
           partially formed characters / escape sequences. */
        return (
            <Box
                component="pre"
                sx={{
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    fontStyle: "italic",
                    fontSize: "var(--font-size-sm)",
                    lineHeight: 1.7,
                    color: "text.primary",
                    display: "inline",
                }}
            >
                {displayed ? `"${displayed}` : ""}
                {/* Blinking cursor */}
                <Box
                    component="span"
                    sx={{
                        display: "inline-block",
                        width: "2px",
                        height: "1em",
                        bgcolor: "primary.main",
                        ml: "2px",
                        verticalAlign: "text-bottom",
                        animation: "cursorBlink 0.7s step-start infinite",
                        "@keyframes cursorBlink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0 },
                        },
                    }}
                />
            </Box>
        );
    };


    const StockCard = ({ stock, onRemove }) => {
        const quantData = getQuantData(stock.ticker);
        const isLoading = loadingRatings[stock.ticker];
        const hasData = factorRatings[stock.ticker];
        const isBullish = quantData.pulse >= 50;

        return (
            <Card
                sx={{
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2.5,
                    transition: "all 0.3s ease",
                    opacity: isLoading ? 0.6 : 1,
                    position: "relative",
                    "&:hover": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    },
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={() => onRemove(stock.ticker)}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: theme.palette.background.default,
                        color: theme.palette.text.secondary,
                        width: 28,
                        height: 28,
                        zIndex: 10,
                        "&:hover": {
                            bgcolor: theme.palette.error.main,
                            color: "#fff",
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>

                {isLoading && (
                    <LinearProgress
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            bgcolor: "transparent",
                            borderRadius: "4px 4px 0 0",
                            "& .MuiLinearProgress-bar": {
                                bgcolor: theme.palette.primary.main,
                            },
                        }}
                    />
                )}

                {/* Header */}
                {/* <Box display="flex" alignItems="flex-start" mb={2.5}> */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                    <Box>
                        <Typography
                            fontSize="var(--font-size-3xl)"
                            fontWeight={800}
                            color={theme.palette.text.primary}
                            letterSpacing="-0.02em"
                            sx={{
                                cursor: "pointer",
                                "&:hover": { color: theme.palette.primary.main },
                            }}
                            onClick={() => navigate(`/stocks/${stock.ticker}/details`)}
                        >
                            {stock.ticker}
                        </Typography>
                        <Box display="flex" gap={0.75} mt={1}>
                            <Button
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openAddToWatchlist(stock.ticker);
                                }}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: "#fff",
                                    fontSize: 10,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    px: 1.25,
                                    py: 0.35,
                                    minWidth: "auto",
                                    borderRadius: 0.75,
                                    "&:hover": { bgcolor: theme.palette.primary.dark },
                                }}
                            >
                                Watchlist
                            </Button>
                        </Box>
                    </Box>

                    <Box textAlign="right">
                        {/* <Button
                            sx={{
                                bgcolor: quantData.overallRating === "Buy" ? "#10b981" :
                                    quantData.overallRating === "Hold" ? "#f59e0b" :
                                        quantData.overallRating === "Sell" ? "#ef4444" : "#64748b",
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                px: 2,
                                py: 0.75,
                                borderRadius: 1,
                                mb: 1,
                                "&:hover": {
                                    bgcolor: quantData.overallRating === "Buy" ? "#059669" :
                                        quantData.overallRating === "Hold" ? "#d97706" :
                                            quantData.overallRating === "Sell" ? "#dc2626" : "#475569",
                                },
                            }}
                        >
                            {hasData ? quantData.overallRating : "N/A"}
                        </Button> */}
                        <Box
                            textAlign="right"
                            sx={{
                                ml: "auto",
                                pr: 7,
                            }}
                        >
                            <Box display="flex" alignItems="baseline" justifyContent="flex-end" gap={0.5}>
                                <Typography
                                    fontSize={32}
                                    fontWeight={700}
                                    color={getColorForGrade(quantData.overallGrade)}
                                >
                                    {hasData ? quantData.overallGrade : "--"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* FA Insight Badge */}
                <Box display="flex" gap={1} mb={2}>
                    <Chip
                        label="FA INSIGHT"
                        size="small"
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? "#334155" : "#e2e8f0",
                            color: theme.palette.mode === 'dark' ? "#94a3b8" : "#64748b",
                            fontSize: 12,
                            height: 20,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                        }}
                    />
                    {hasData && (
                        <Chip
                            label={isBullish ? `Bullish (${fmtL(quantData.pulse, 0)})` : `Bearish (${fmtL(quantData.pulse, 0)})`}
                            size="small"
                            sx={{
                                bgcolor: isBullish ? "#10b981" : "#ef4444",
                                color: "#fff",
                                fontSize: 12,
                                height: 20,
                                fontWeight: 700,
                            }}
                        />
                    )}
                </Box>

                {/* Pulse Analysis Slider */}
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={0.75}>
                        <Typography fontSize="var(--font-size-xs)" fontWeight={700} color={theme.palette.text.primary} letterSpacing="0.05em">
                            BEARISH
                        </Typography>
                        <Typography fontSize="var(--font-size-xs)" fontWeight={700} color={theme.palette.text.primary} letterSpacing="0.05em">
                            PULSE ANALYSIS
                        </Typography>
                        <Typography fontSize="var(--font-size-xs)" fontWeight={700} color={theme.palette.text.primary} letterSpacing="0.05em">
                            BULLISH
                        </Typography>
                    </Box>
                    <Box position="relative">
                        <Box
                            sx={{
                                height: 8,
                                borderRadius: 1,
                                background: "linear-gradient(to right, #ef4444 0%, #64748b 50%, #10b981 100%)",
                            }}
                        />
                        {hasData && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: `${quantData.pulse}%`,
                                    transform: "translate(-50%, -50%)",
                                    width: 20,
                                    height: 20,
                                    bgcolor: "#fff",
                                    borderRadius: "50%",
                                    border: `3px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 0 0 4px ${theme.palette.primary.main}40`,
                                }}
                            />
                        )}
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                        <Typography fontSize="var(--font-size-xs)" color={theme.palette.text.secondary}>
                            0
                        </Typography>
                        <Typography fontSize="var(--font-size-xs)" color={theme.palette.text.secondary}>
                            50
                        </Typography>
                        <Typography fontSize="var(--font-size-xs)" color={theme.palette.text.secondary}>
                            100
                        </Typography>
                    </Box>
                </Box>

                {/* Factor Ratings */}
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2.5}>
                    {[
                        { label: "VALUATION", score: quantData.valuation, grade: quantData.valuationGrade },
                        { label: "GROWTH", score: quantData.growth, grade: quantData.growthGrade },
                        // { label: "FINANCIAL", score: quantData.financial, grade: quantData.financialGrade },
                        {
                            label: ACTIVE_MODEL === "return" ? "PROFITABILITY" : "QUALITY",
                            score: quantData.financial,
                            grade: quantData.financialGrade
                        },
                        { label: "RISK", score: quantData.risk, grade: quantData.riskGrade },
                        { label: "MOMENTUM", score: quantData.momentum, grade: quantData.momentumGrade },
                        // { label: "SENTIMENT", score: 0, grade: "N/A" },
                        /* 6th factor — split out of Risk (now price risk) */
                        {
                            label: BALANCE_SHEET_FACTOR.labelUpper,
                            score: quantData.balanceSheet,
                            grade: quantData.balanceSheetGrade,
                        },
                    ].map((factor) => (
                        <Box key={factor.label}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <Typography
                                    fontSize="var(--font-size-xs)"
                                    fontWeight={700}
                                    color={theme.palette.text.primary}
                                    letterSpacing="0.05em"
                                >
                                    {factor.label}
                                </Typography>
                                <Tooltip
                                    title={
                                        <Box>
                                            {/* Score badge */}
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={2}
                                                mb={0.75}
                                                pb={0.75}
                                                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                                            >
                                                <Typography variant="label" sx={{ color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                                    {factor.label}
                                                </Typography>
                                                {/* <Typography
                                                    variant="label"
                                                    fontWeight={700}
                                                    sx={{
                                                        color: hasData && factor.grade !== "N/A"
                                                            ? getColorForGrade(factor.grade)
                                                            : "#94a3b8",
                                                    }}
                                                >
                                                    {hasData ? `${fmtL(factor.score, 1)} / 100` : "N/A"}
                                                </Typography> */}
                                            </Box>
                                            {/* Description */}
                                            <Typography variant="caption" sx={{ color: "#e2e8f0" }}>
                                                {FACTOR_TOOLTIPS[factor.label] ?? `${factor.label} score explanation`}
                                            </Typography>
                                        </Box>
                                    }
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#1f2937",
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1.5,
                                                px: 1.5,
                                                py: 1,
                                                maxWidth: 260,
                                            },
                                        },
                                        arrow: {
                                            sx: { color: theme.palette.mode === "dark" ? "#1e293b" : "#1f2937" },
                                        },
                                    }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                            cursor: "help",
                                            "&:hover": { color: theme.palette.primary.main },
                                            transition: "color 0.2s",
                                        }}
                                    />
                                </Tooltip>
                                <Typography
                                    fontSize="var(--font-size-sm)"
                                    fontWeight={700}
                                    color={hasData && factor.grade !== "N/A" ? getColorForGrade(factor.grade) : theme.palette.text.secondary}
                                    ml="auto"
                                >
                                    {hasData ? factor.grade : "--"}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={hasData ? Math.min(factor.score, 100) : 0}
                                sx={{
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.mode === 'dark' ? "#1e293b" : "#e2e8f0",
                                    "& .MuiLinearProgress-bar": {
                                        bgcolor: hasData && factor.grade !== "N/A" ? getColorForGrade(factor.grade) : theme.palette.text.secondary,
                                        borderRadius: 1,
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                {/* Divider */}
                <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />

                {/* Analysis Section */}
                <Box>
                    <Typography
                        fontSize="var(--font-size-xs)"
                        fontWeight={700}
                        color={theme.palette.text.secondary}
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                        mb={1}
                    >
                        FA Analysis
                    </Typography>

                    {loadingAnalysis[stock.ticker] ? (
                        <>
                            <Box
                                sx={{
                                    height: 14,
                                    bgcolor: theme.palette.mode === 'dark' ? "#1e293b" : "#e2e8f0",
                                    borderRadius: 1,
                                    mb: 1,
                                    animation: "pulse 1.5s ease-in-out infinite",
                                    "@keyframes pulse": {
                                        "0%, 100%": { opacity: 1 },
                                        "50%": { opacity: 0.5 },
                                    },
                                }}
                            />
                            <Box
                                sx={{
                                    height: 14,
                                    bgcolor: theme.palette.mode === 'dark' ? "#1e293b" : "#e2e8f0",
                                    borderRadius: 1,
                                    width: "80%",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        </>
                    ) : factorAnalysis[stock.ticker]?.narrative ? (
                        <TypingNarrative text={factorAnalysis[stock.ticker].narrative} speed={6} />
                    ) : (
                        <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary}>
                            No analysis available.
                        </Typography>
                    )}
                </Box>
            </Card>
        );
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                px: { xs: 2, md: 4 },
                py: 3.5,
            }}
        >
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={3}
                flexWrap="wrap"
                gap={2}
            >
                <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                        variant='title'
                        fontWeight="var(--font-weight-bold)"
                        className="text-primary"
                        sx={{ color: 'var(--color-text-primary)' }}
                        mb={0.25}
                        letterSpacing="-0.02em"
                    >
                        FA Factor Ratings
                    </Typography>
                    <Typography variant="subtitle" fontWeight="var(--font-weight-normal)" sx={{ color: 'var(--color-text-muted)' }}>
                        Multi-factor benchmarking with real-time news sentiment.
                    </Typography>
                </Box>
            </Box>

            {/* Search Bar */}
            <Box display="flex" gap={2} mb={3} position="relative">
                <Box ref={searchRef} sx={{ flex: 1, position: "relative" }}>
                    <TextField
                        fullWidth
                        placeholder={getPlaceholder()}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter" && searchInput.includes(',')) {
                                handleExecute();
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Autocomplete Dropdown */}
                    {showSearchDropdown && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                mt: 1,
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                boxShadow: theme.shadows[3],
                                zIndex: 1000,
                                maxHeight: 320,
                                overflow: "hidden",
                            }}
                        >
                            {searchLoading ? (
                                <Box p={2}>
                                    <Typography fontSize="var(--font-size-base)" color={theme.palette.text.primary}>
                                        Searching for "{searchInput}"...
                                    </Typography>
                                </Box>
                            ) : searchError ? (
                                <Box p={2}>
                                    <Typography fontSize="var(--font-size-base)" color={theme.palette.error.main}>
                                        {searchError}
                                    </Typography>
                                </Box>
                            ) : searchResults.length === 0 ? (
                                <Box p={2}>
                                    <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary}>
                                        No results found for "{searchInput}"
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        px={2}
                                        py={1}
                                        bgcolor={theme.palette.mode === 'dark' ? "#1e293b" : "#f9fafb"}
                                        borderBottom={`1px solid ${theme.palette.divider}`}
                                    >
                                        <Typography
                                            fontSize="var(--font-size-sm)"
                                            fontWeight={600}
                                            color={theme.palette.text.secondary}
                                            textTransform="uppercase"
                                            letterSpacing="0.05em"
                                        >
                                            Symbols
                                        </Typography>
                                    </Box>
                                    <Box sx={{ maxHeight: 256, overflow: "auto" }}>
                                        {searchResults.map((stock, index) => (
                                            <Box
                                                key={stock.ticker}
                                                onClick={() => handleStockSelect(stock)}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    cursor: "pointer",
                                                    borderBottom:
                                                        index !== searchResults.length - 1
                                                            ? `1px solid ${theme.palette.divider}`
                                                            : "none",
                                                    "&:hover": {
                                                        bgcolor: theme.palette.mode === 'dark' ? "#1e293b" : "#f9fafb",
                                                    },
                                                }}
                                            >
                                                <Box display="flex" gap={2} alignItems="center">
                                                    <Typography
                                                        fontSize="var(--font-size-sm)"
                                                        fontWeight={600}
                                                        color={theme.palette.text.primary}
                                                        minWidth={60}
                                                    >
                                                        {stock.ticker}
                                                    </Typography>
                                                    <Typography
                                                        fontSize="var(--font-size-sm)"
                                                        color={theme.palette.text.secondary}
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {stock.company_name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                <Button
                    onClick={handleExecute}
                    disabled={!searchInput.trim() || !searchInput.includes(',')}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "none",
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        minWidth: 140,
                        "&:hover": {
                            bgcolor: theme.palette.primary.dark,
                        },
                        "&:disabled": {
                            bgcolor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                        },
                    }}
                >
                    Batch Execute
                </Button>
            </Box>

            {/* Stock Cards Grid */}
            {loading ? (
                <Box py={10} textAlign="center">
                    <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.primary}>
                        Loading recently viewed stocks...
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Searched Stocks */}
                    {searchedStocks.length > 0 && (
                        <Box mb={4}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography
                                    fontSize={24}
                                    fontWeight={700}
                                    color={theme.palette.text.primary}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                    Analyzed Stocks ({searchedStocks.length})
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearSearch}
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: 12,
                                        textTransform: "none",
                                        "&:hover": {
                                            color: theme.palette.text.primary,
                                            bgcolor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                            <Box
                                display="grid"
                                gridTemplateColumns={{
                                    xs: "1fr",
                                    md: "repeat(3, 1fr)",
                                    lg: "repeat(3, 1fr)",
                                }}
                                gap={3}
                            >
                                {searchedStocks.map((stock) => (
                                    <StockCard
                                        key={stock.ticker}
                                        stock={stock}
                                        onRemove={handleRemoveSearched}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Recently Viewed Stocks */}
                    {recentStocks.length === 0 && searchedStocks.length === 0 ? (
                        <Box
                            py={10}
                            textAlign="center"
                            sx={{
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                            <Typography fontSize={20} fontWeight={600} color={theme.palette.text.primary} mb={1}>
                                Search Tickers to Find FA Factor Ratings
                            </Typography>
                            <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary}>
                                Use the search bar above to analyze stocks and view their multi-factor FA Ratings
                            </Typography>
                        </Box>
                    ) : recentStocks.length > 0 ? (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography
                                    fontSize="var(--font-size-lg)"
                                    fontWeight={700}
                                    color={theme.palette.text.primary}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    Recently Viewed
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearRecentlyViewed}
                                    disabled={clearingRecent}
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: 12,
                                        textTransform: "none",
                                        "&:hover": {
                                            color: theme.palette.text.primary,
                                            bgcolor: theme.palette.action.hover,
                                        },
                                        "&:disabled": {
                                            color: theme.palette.action.disabled,
                                        },
                                    }}
                                >
                                    {clearingRecent ? "Clearing..." : "Clear All"}
                                </Button>
                            </Box>
                            <Box
                                display="grid"
                                gridTemplateColumns={{
                                    xs: "1fr",
                                    md: "repeat(3, 1fr)",
                                    lg: "repeat(3, 1fr)",
                                }}
                                gap={3}
                            >
                                {recentStocks.map((stock) => (
                                    <StockCard
                                        key={stock.ticker}
                                        stock={stock}
                                        onRemove={handleRemoveRecent}
                                    />
                                ))}
                            </Box>
                        </>
                    ) : null}

                    {/* Methodology Section */}
                    <Box
                        sx={{
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            p: 3,
                            my: 3,
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                            <Box flex={1}>
                                <Typography
                                    fontSize="var(--font-size-lg)"
                                    fontWeight="var(--font-weight-semibold)"
                                    sx={{ color: 'var(--color-text-primary)' }}
                                    mb={1}
                                    letterSpacing="-0.01em">
                                    Institutional Weighting Methodology
                                </Typography>
                                <Typography fontSize="var(--font-size-base)"
                                    color={theme.palette.text.secondary} fontWeight={400} letterSpacing="0.05em">
                                    How the multi-factor blended score is synthesized
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            display="grid"
                            gridTemplateColumns="repeat(3, 1fr)"
                            gap={2}
                        >
                            {[
                                { label: "GROWTH", weight: "25%", color: "#3b82f6" },
                                // { label: "FINANCIAL", weight: "20%", color: "#10b981" },
                                {
                                    label: ACTIVE_MODEL === "return" ? "PROFITABILITY" : "QUALITY",
                                    weight: "20%",
                                    color: "#10b981"
                                },
                                { label: "VALUATION", weight: "25%", color: "#f59e0b" },
                                { label: "MOMENTUM", weight: "10%", color: "#8b5cf6" },
                                { label: "RISK", weight: "20%", color: "#ef4444" },
                                // { label: "SENTIMENT", weight: "10%", color: "#06b6d4" },
                            ].map((factor) => (
                                <Box
                                    key={factor.label}
                                    sx={{
                                        bgcolor: theme.palette.background.default,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1.5,
                                        p: 2,
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        fontSize="var(--font-size-sm)"
                                        fontWeight={600}
                                        color={theme.palette.text.secondary}
                                        letterSpacing="0.05em"
                                        mb={1}
                                    >
                                        {factor.label}
                                    </Typography>
                                    <Typography
                                        fontSize={32}
                                        fontWeight={700}
                                        color={factor.color}
                                        lineHeight={1}
                                    >
                                        {factor.weight}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </>
            )}
            <AddToWatchlistDialog
                open={watchlistDialogOpen}
                onClose={closeAddToWatchlist}
                ticker={selectedTicker}
            />
        </Box>
    );
};

export default QuantFactorRating;